import { SIGNAL_STORM_START_SEC } from '../../data/signalStorm.js';
import type { MatchOutcome, MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { PlayerId } from '../ids.js';
import { otherPlayer } from '../ids.js';

// V1.5 win conditions, evaluated each tick after movement/combat/capture/economy.
// Outcome is sticky once set — never overwritten — so subsequent ticks observe
// the same MatchState.outcome until the host stops or restarts. The host emits
// a single 'match_ended' lifecycle event the first tick outcome flips from null.
//
// Rules:
//   1. If any Core's hp <= 0 → core_destroyed; loser = that Core's owner.
//   2. Else if phase is 'overtime' → see overtime block below.
//   3. Else if matchSeconds >= matchLengthSec:
//        - strict node lead → dominance, reason='nodes'.
//        - tied             → enter overtime (phase flips, no outcome yet).
//   4. V1.5+ Slice 8 — if phase is 'regulation' AND matchSeconds crosses
//      SIGNAL_STORM_START_SEC, transition to 'signal_storm'. The storm
//      runs through to matchLengthSec; the existing dominance/overtime
//      logic at expiry routes through the storm phase first.
//
// Overtime (per docs/02_GAME_DESIGN.md §1):
//   - 60 s window starting at regulation expiry.
//   - First side to hold strictly more relays for OVERTIME_LEAD_REQUIRED_SEC
//     continuous seconds wins, reason='overtime_dominance'. The lead counter
//     resets on tie or lead flip.
//   - If the 60 s window expires without that condition, tiebreak by total
//     Core HP (reason='core_hp_tiebreak'), then by peak income/sec
//     (reason='peak_income_tiebreak'). Total tie picks PLAYER_ZERO so a
//     winner is always declared (this should be unreachable in normal play).

export const OVERTIME_DURATION_SEC = 60;
export const OVERTIME_LEAD_REQUIRED_SEC = 10;

export function winConditionSystem(state: MatchState): void {
  if (state.outcome !== null) return;

  const destroyed = findDestroyedCore(state);
  if (destroyed !== null) {
    state.outcome = {
      kind: 'core_destroyed',
      loser: destroyed,
      winner: otherPlayer(destroyed),
      tick: state.tick,
    };
    return;
  }

  if (state.phase === 'overtime') {
    tickOvertime(state);
    return;
  }

  // V1.5+ Slice 8 — Signal Storm Endgame transition. Fires once on the
  // rising edge regulation → signal_storm at SIGNAL_STORM_START_SEC. The
  // matchSeconds < matchLengthSec guard is a defensive check for tests /
  // dev configs that ship with a match length ≤ SIGNAL_STORM_START_SEC
  // (the storm-then-end window would have zero duration in that case;
  // skip straight to the timer expiry).
  if (
    state.phase === 'regulation' &&
    state.matchSeconds >= SIGNAL_STORM_START_SEC &&
    state.matchSeconds < state.matchLengthSec
  ) {
    state.phase = 'signal_storm';
    state.signalStormStartedAtSec = state.matchSeconds;
  }

  if (state.matchSeconds >= state.matchLengthSec) {
    const heldZero = countNodesHeldBy(state, 0);
    const heldOne = countNodesHeldBy(state, 1);
    if (heldZero !== heldOne) {
      state.outcome = {
        kind: 'dominance',
        winner: heldZero > heldOne ? 0 : 1,
        reason: 'nodes',
        tick: state.tick,
      };
      return;
    }
    // Tied at regulation — enter overtime. Outcome stays null; the host detects
    // the phase transition and emits 'overtime_started' on the next broadcast.
    state.phase = 'overtime';
    state.overtimeStartedAtSec = state.matchSeconds;
    state.overtimeLeadPlayer = null;
    state.overtimeLeadHeldSec = 0;
  }
}

function tickOvertime(state: MatchState): void {
  const heldZero = countNodesHeldBy(state, 0);
  const heldOne = countNodesHeldBy(state, 1);

  if (heldZero === heldOne) {
    state.overtimeLeadPlayer = null;
    state.overtimeLeadHeldSec = 0;
  } else {
    const leader: PlayerId = heldZero > heldOne ? 0 : 1;
    if (state.overtimeLeadPlayer === leader) {
      state.overtimeLeadHeldSec += SECONDS_PER_TICK;
    } else {
      state.overtimeLeadPlayer = leader;
      state.overtimeLeadHeldSec = SECONDS_PER_TICK;
    }
    if (state.overtimeLeadHeldSec >= OVERTIME_LEAD_REQUIRED_SEC) {
      state.outcome = {
        kind: 'overtime',
        winner: leader,
        reason: 'overtime_dominance',
        tick: state.tick,
      };
      return;
    }
  }

  const otStart = state.overtimeStartedAtSec ?? state.matchLengthSec;
  if (state.matchSeconds >= otStart + OVERTIME_DURATION_SEC) {
    state.outcome = resolveOvertimeTiebreak(state);
  }
}

function resolveOvertimeTiebreak(state: MatchState): MatchOutcome {
  const hpZero = totalCoreHp(state, 0);
  const hpOne = totalCoreHp(state, 1);
  if (hpZero !== hpOne) {
    return {
      kind: 'overtime',
      winner: hpZero > hpOne ? 0 : 1,
      reason: 'core_hp_tiebreak',
      tick: state.tick,
    };
  }
  const peakZero = state.players[0].peakIncomePerSec;
  const peakOne = state.players[1].peakIncomePerSec;
  if (peakZero !== peakOne) {
    return {
      kind: 'overtime',
      winner: peakZero > peakOne ? 0 : 1,
      reason: 'peak_income_tiebreak',
      tick: state.tick,
    };
  }
  // Total tie — degenerate; pick player 0 deterministically. The third tiebreak
  // exists so a winner is always declared.
  return {
    kind: 'overtime',
    winner: 0,
    reason: 'peak_income_tiebreak',
    tick: state.tick,
  };
}

function findDestroyedCore(state: MatchState): PlayerId | null {
  for (const core of Object.values(state.cores)) {
    if (core.hp <= 0) return core.ownerId;
  }
  return null;
}

function countNodesHeldBy(state: MatchState, playerId: PlayerId): number {
  let n = 0;
  for (const node of Object.values(state.controlNodes)) {
    if (node.ownerId === playerId) n += 1;
  }
  return n;
}

function totalCoreHp(state: MatchState, playerId: PlayerId): number {
  let hp = 0;
  for (const core of Object.values(state.cores)) {
    if (core.ownerId === playerId) hp += core.hp;
  }
  return hp;
}
